import { arrayify } from '../array/array';
import { isUnpublished, isUntranslated } from '../category/category';
import { ITEM_TYPE_SPR } from '../types/itemTypes';
import {
  NODE_TYPE_CATALOG,
  NODE_TYPE_CATEGORY,
  NODE_TYPE_ITEM,
  NON_NODE_ITEM_TYPES,
} from '../types/nodeTypes';
import {
  validateGlobal as validateCatalogGlobal,
  validateMarket as validateCatalogMarket,
} from '../validation/catalog';
import {
  validateGlobal as validateCategoryGlobal,
  validateMarket as validateCategoryMarket,
} from '../validation/category';
import type { Catalog } from '../../entities/Catalog';
import type { Category } from '../../entities/Category';
import type { Item } from '../../entities/Item';
import type { NodeBase, NodeBaseItem } from '../../entities/Node';

const idSeparator = ':';

export type DestructedId = {
  value?: string;
  catalogId?: string;
  categoryId?: string;
  itemId?: string;
  [key: string]: string | undefined;
};

export const createId = ({
  parentId,
  type,
  contentId = type,
}: {
  parentId?: string;
  type: string;
  contentId?: string;
}): string => {
  const parent = parentId ? `${parentId}${idSeparator}` : '';
  const content = `${type}_${contentId}`;
  return parent + content;
};

export const destructId = (nodeId: string | undefined | null): DestructedId => {
  if (!nodeId || !nodeId.length) {
    return {};
  }
  let splitNode = nodeId.split(idSeparator).pop();
  if (!splitNode) {
    splitNode = nodeId;
  }
  const [type, id] = splitNode.split('_');

  return {
    [type === NODE_TYPE_CATALOG ? 'catalogId' : `${type}Id`]: id,
    value: id,
  };
};

export const getLeafCategoryForItem = (parentId: string | undefined): string => {
  if (!parentId) return '';

  const indexTerm = 'category_';
  const start = parentId.lastIndexOf(indexTerm);
  const end = parentId.lastIndexOf(':');
  // create new string from last 'category_' and last ':'.
  // if end is smaller than start, its not a grouped item
  const lastCategory =
    start > -1
      ? parentId.substring(
          start + indexTerm.length,
          end > start ? end : parentId.length,
        )
      : '';

  return lastCategory;
};

export const getPathById = (id: string): string[] =>
  id
    .split(idSeparator)
    .reduce<
      string[]
    >((path, _, idx, ids) => [...path, ids.slice(0, idx + 1).join(idSeparator)], []);

export function isNodeExpandable(node: NodeBase): boolean {
  const n = node as NodeBase & { itemType?: string };
  return (
    !node.isNew &&
    (node.type !== NODE_TYPE_ITEM ||
      n.itemType === ITEM_TYPE_SPR ||
      !!node.item?.countrySpecificItemCount) &&
    !NON_NODE_ITEM_TYPES.includes(node.type)
  );
}

export function hasLoadMoreItems(node: NodeBase): boolean {
  if (!node.category?.categoryId || !node.expanded) {
    return false;
  }
  return node.category.childItemCount > node.children?.length;
}

export function hasPasteOption(node: { type: string }): boolean {
  return [NODE_TYPE_CATALOG, NODE_TYPE_CATEGORY].includes(node.type);
}

export function hasSameContent(
  nodeItem: NodeBaseItem,
  nodeItems: NodeBaseItem | NodeBaseItem[],
): boolean {
  const [key, value] = getContentIdPairsFromPathId(nodeItem.node.id);
  if (!key) return false;
  return arrayify(nodeItems).some((ni) => destructId(ni.node.id)[key] === value);
}

export function getCatalogTitle(catalog: Catalog, languageCode: string): string {
  return (
    catalog.catalogIdentifierTranslations?.[languageCode] ||
    catalog.globalCatalogIdentifier ||
    ''
  );
}

type CategoryTitleFields = {
  globalCategoryName?: string | null;
  categoryNameTranslationsVerified?: Record<string, string | null> | null;
  categoryNameTranslationApproved?: boolean;
  categoryNameTranslations?: Record<string, string | null> | null;
};

export function getCategoryTitle(
  category: CategoryTitleFields,
  languageCode = 'en',
): string {
  const globalCategoryName = category.globalCategoryName || '';
  const title =
    category.categoryNameTranslationsVerified?.[languageCode] || globalCategoryName;
  if (category.categoryNameTranslationApproved) {
    return category.categoryNameTranslations?.[languageCode] || title;
  }
  return title;
}

export function validateCatalog(
  { catalog }: { catalog: Catalog },
  isGlobal: boolean,
  isDirty: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { isInvalid: boolean; errors?: Record<string, any> } {
  if (!isDirty) {
    return {
      isInvalid: false,
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errors: Record<string, any> = isGlobal
    ? validateCatalogGlobal(catalog)
    : validateCatalogMarket(catalog);
  return {
    isInvalid: Object.values(errors).some((e) => getBooleanValue(e)),
    errors,
  };
}

export function validateCategory(
  { category, isNew = false }: { category: Category; isNew?: boolean },
  isGlobal: boolean,
  isMaster: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { isInvalid: boolean; errors: Record<string, any> } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errors: Record<string, any> = isGlobal
    ? validateCategoryGlobal(category, isNew, isMaster)
    : validateCategoryMarket(category);
  return {
    isInvalid: Object.values(errors).some((e) => getBooleanValue(e)),
    errors,
  };
}

export function hasUnpublished(node: NodeBase): boolean {
  return isUnpublished(node.category);
}

export function hasUntranslated(node: NodeBase): boolean {
  return isUntranslated(node.category);
}

function getBooleanValue(val: unknown): boolean {
  if (typeof val === 'boolean') {
    return val;
  }
  if (val !== null && typeof val === 'object') {
    return Object.values(val).some((v) => getBooleanValue(v));
  }
  return !!val;
}

export function getNodeDataContent(
  node: NodeBase,
): Catalog | Category | Item | null {
  switch (node.type) {
    case NODE_TYPE_CATALOG:
      return node.catalog ?? null;
    case NODE_TYPE_CATEGORY:
      return node.category;
    case NODE_TYPE_ITEM:
      return node.item;
    default:
      return null;
  }
}

export function getNodeDataId(node: NodeBase): string | undefined {
  let dataId: string | null | undefined;
  switch (node.type) {
    case NODE_TYPE_CATALOG:
      dataId = node.catalog?.catalogId;
      break;
    case NODE_TYPE_CATEGORY:
      dataId = node.category.categoryId;
      break;
    case NODE_TYPE_ITEM:
      dataId = node.item.itemNo;
      break;
    default:
      dataId = null;
      break;
  }

  return dataId ? `${node.type}-${dataId}` : undefined;
}

export function getContentIdPairsFromPathId(
  pathedId?: string,
  key?: string,
): [string, string] | [] {
  const pairs =
    (Object.entries(destructId(pathedId)).find(([prop]) => prop !== 'value') as
      | [string, string]
      | undefined) || [];
  if (pairs.length === 0) return [];
  if (!key || pairs[0] === key) {
    return pairs;
  }
  return [];
}

export function getContentIdFromProperty(
  ni: NodeBaseItem | undefined | null,
  property: string | undefined,
): unknown {
  if (!property) return undefined;
  return ni?.node?.[property.slice(0, -2) as keyof NodeBase]?.[property as never];
}

export function comparePathedIds(
  leftId: string | undefined,
  rightId: string | undefined,
): boolean {
  const [leftKey, leftValue] = getContentIdPairsFromPathId(leftId);
  const [rightKey, rightValue] = getContentIdPairsFromPathId(rightId);
  return leftKey === rightKey && leftValue === rightValue;
}
